import { ReactNode } from "react";
import {
  HeaderGroup,
  Row,
  TableBodyPropGetter,
  TablePropGetter,
} from "react-table";

import { StickyActionCell } from "./DataTable/components/StickyMenuColumn";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadContent,
  TableHeader,
  TableRow,
} from "./Table";
import cn from "@/helpers/classNames";

interface TableRendererProps<T extends object> {
  getTableProps: (propGetter?: TablePropGetter<T>) => any;
  getTableBodyProps: (propGetter?: TableBodyPropGetter<T>) => any;
  headerGroups: HeaderGroup<T>[];
  rows: Row<T>[];
  prepareRow: (row: Row<T>) => void;
  columns: { length: number };
  empty?: ReactNode;
  onRowClick?: (
    row: T,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
  isSortable?: boolean;
  rowClassName?: (row: T) => string | undefined;
  tableClassName?: string;
}

export function TableRenderer<T extends object>({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  columns,
  empty,
  onRowClick,
  isSortable,
  rowClassName,
  tableClassName,
}: TableRendererProps<T>) {
  const tableProps = getTableProps();
  return (
    <Table {...tableProps} className={cn(tableProps.className, tableClassName)}>
      <TableHeader>
        {headerGroups.map((headerGroup) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  {...header.getHeaderProps({
                    style: {
                      maxWidth: header.maxWidth,
                      minWidth: header.minWidth,
                      width: header.width,
                    },
                  })}
                >
                  <TableHeadContent
                    isSorted={header.isSorted}
                    isSortedDesc={header.isSortedDesc}
                    canSort={isSortable && header.canSort}
                    {...header.getSortByToggleProps?.()}
                  >
                    {header.render("Header")}
                  </TableHeadContent>
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody {...getTableBodyProps()}>
        {empty && rows.length === 0 ? (
          <TableRow>
            <TableCell className="p-12" colSpan={columns.length}>
              {empty}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            prepareRow(row);
            const rowCls = rowClassName?.(row.original);
            return (
              <TableRow
                className={rowCls}
                onClick={
                  typeof onRowClick === "function"
                    ? (e) => onRowClick(row.original, e)
                    : undefined
                }
                {...row.getRowProps()}
              >
                {row.cells.map((cell) => {
                  if (cell.column.isStickyMenuColumn) {
                    return (
                      <StickyActionCell
                        className={rowCls}
                        {...cell.getCellProps({
                          style: {
                            maxWidth: cell.column.maxWidth,
                            minWidth: cell.column.minWidth,
                            width: cell.column.width,
                          },
                        })}
                      >
                        {cell.render("Cell")}
                      </StickyActionCell>
                    );
                  }
                  return (
                    <TableCell
                      {...cell.getCellProps({
                        style: {
                          maxWidth: cell.column.maxWidth,
                          minWidth: cell.column.minWidth,
                          width: cell.column.width,
                        },
                      })}
                    >
                      {cell.render("Cell")}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
